import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DragIndicator, OpenInNew, PushPin } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  OrderSnapshot,
  PinnedItem,
  ProductSnapshot,
  useWorkspaceStore,
} from "../../store/useWorkspaceStore";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { Copyable } from "../copyable/Copyable";

type Props = {
  item: PinnedItem;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
};

export const WorkspaceItemCard = ({ item, dragHandleProps }: Props) => {
  const { removeItem, setItemNote } = useWorkspaceStore();
  const navigate = useNavigate();

  const isOrder = item.type === "order";
  const orderSnap = item.snapshot as OrderSnapshot;
  const productSnap = item.snapshot as ProductSnapshot;

  const title = isOrder
    ? orderSnap.status
    : (productSnap.itemName ?? productSnap.asin);
  const subtitle = isOrder ? orderSnap.amazonOrderId : productSnap.asin;
  const imageUrl = isOrder ? orderSnap.imageUrl : productSnap.imageUrl;

  const handleNavigate = () => {
    navigate(
      isOrder
        ? `/order-detail/${orderSnap.id}`
        : `/inventory/product/${productSnap.asin}`,
    );
    useWorkspaceStore.getState().close();
  };

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box display="flex" alignItems="flex-start" gap={0.5} mb={1}>
          <Box
            {...dragHandleProps}
            sx={{
              color: "text.disabled",
              cursor: "grab",
              mt: 0.25,
              flexShrink: 0,
            }}
          >
            <DragIndicator />
          </Box>

          {imageUrl && (
            <Box
              component="img"
              src={imageUrl}
              alt={title}
              sx={{
                width: 44,
                height: 44,
                objectFit: "contain",
                borderRadius: 1,
                flexShrink: 0,
              }}
            />
          )}

          <Box flexGrow={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={0.5} mb={0.25}>
              <Chip
                label={isOrder ? "Sipariş" : "Ürün"}
                size="small"
                sx={{ height: 18, fontSize: "0.65rem" }}
              />
            </Box>

            <Tooltip title={title}>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                sx={{ maxWidth: 160 }}
              >
                {title}
              </Typography>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" noWrap>
              <Copyable initialLabel={subtitle} />
            </Typography>
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={0.5}
          >
            <Tooltip title="Sayfaya git">
              <IconButton size="small" onClick={handleNavigate}>
                <OpenInNew fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Kaldır">
              <IconButton
                size="small"
                color="error"
                onClick={() => removeItem(item.uid)}
              >
                <PushPin fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TextField
          multiline
          minRows={2}
          maxRows={5}
          fullWidth
          size="small"
          placeholder="Not ekle..."
          value={item.note}
          onChange={(e) => setItemNote(item.uid, e.target.value)}
          sx={{ "& .MuiInputBase-root": { fontSize: "0.8rem" } }}
        />
      </CardContent>
    </Card>
  );
};
